import React from "react";
import { AccountingFrequencyEnum } from "../../portal/src/types.generated";
import { formatDateTimeLocal, formatWeekdayLong, formatYmdInTimeZone, formatYmdLocal, zonedTimeToUtc } from "../../common/src/dateIntl";

const swedenTimeZone = "Europe/Stockholm";

const isYmd = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

function parseDateInput(value: any): Date {
    if (value instanceof Date) {
        return value;
    }
    if (isYmd(value)) {
        const [y, m, d] = value.split("-").map((x) => Number(x));
        return new Date(y, m - 1, d, 0, 0, 0, 0);
    }
    return new Date(value);
}

function addDaysLocal(value: any, addDays?: number): Date {
    const date = parseDateInput(value);
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + (addDays ?? 0));
    return copy;
}

function addDaysToYmdUtc(ymd: string, addDays: number): string {
    const [y, m, d] = ymd.split("-").map((x) => Number(x));
    const utc = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
    utc.setUTCDate(utc.getUTCDate() + addDays);
    return utc.toISOString().slice(0, 10);
}

function formatHmLocal(date: Date): string {
    const parts = new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
    return `${hour}:${minute}`;
}

function formatMonthDayYearLocal(date: Date): string {
    return new Intl.DateTimeFormat("en-US", { month: "long", day: "2-digit", year: "numeric" }).format(date);
}

export const dateFormater = (date: string): React.ReactElement => {
    //YYYY-MM-DD HH:mm:ss (local time)
    const formatted = date ? formatDateTimeLocal(date) : null;
    return <div>{formatted ? <time dateTime={date}>{formatted}</time> : null}</div>;
};

export const dateFormaterShort = (date: string): React.ReactElement => {
    //YYYY-MM-DD (local time)
    const formatted = date ? formatYmdLocal(date) : null;
    return <div>{formatted ? <time dateTime={date}>{formatted}</time> : null}</div>;
};

export const serializeDate = (value: any, format = "YYYY-MM-DD", addDays?: number): string => {
    const d = typeof addDays === "number" ? addDaysLocal(value, addDays) : parseDateInput(value);

    switch (format) {
        case "YYYY-MM-DD":
            return formatYmdLocal(d);
        case "YYYY-MM-DD HH:mm:ss":
            return formatDateTimeLocal(d);
        case "dddd":
            return formatWeekdayLong(d);
        case "MMMM DD, YYYY":
            return formatMonthDayYearLocal(d);
        case "MMMM DD, YYYY HH:mm":
            return `${formatMonthDayYearLocal(d)} ${formatHmLocal(d)}`;
        default:
            // We only support the formats actually used in the codebase.
            throw new Error(`serializeDate: unsupported format "${format}"`);
    }
};

/**
 * @deprecated Please stop using this function. We do not wanna have Sweden specific code.
 */
export const serializeSwedenDate = (value: any, format = "YYYY-MM-DD", addDays?: number): string => {
    const baseInstant = isYmd(value) ? zonedTimeToUtc(value, swedenTimeZone) : parseDateInput(value);
    const baseYmd = formatYmdInTimeZone(baseInstant, swedenTimeZone);
    const ymd = typeof addDays === "number" ? addDaysToYmdUtc(baseYmd, addDays) : baseYmd;

    if (format !== "YYYY-MM-DD") {
        throw new Error(`serializeSwedenDate: unsupported format "${format}"`);
    }
    return ymd;
};
/**
 * @deprecated Please stop using this function. We do not wanna have Sweden specific code.
 */
export const serializeSwedenDateIsoString = (value: any, addDays?: number): string => {
    const baseInstant = isYmd(value) ? zonedTimeToUtc(value, swedenTimeZone) : parseDateInput(value);
    const baseYmd = formatYmdInTimeZone(baseInstant, swedenTimeZone);
    const ymd = typeof addDays === "number" ? addDaysToYmdUtc(baseYmd, addDays) : baseYmd;
    return zonedTimeToUtc(ymd, swedenTimeZone).toISOString();
};

export const daysFromTodayCount = (date: any): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const target = parseDateInput(date);
    target.setHours(0, 0, 0, 0);

    return (today.getTime() - target.getTime()) / 86400000;
};

export const yesterday = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return formatYmdLocal(d);
};

export const previousAccountingPeriodYear = (date: string, frequency: AccountingFrequencyEnum): string => {
    const d = parseDateInput(date);

    if (frequency === AccountingFrequencyEnum.Quarterly) {
        const quarterStartMonth = Math.floor(d.getMonth() / 3) * 3;
        const previousQuarterEndMonth = quarterStartMonth - 1; // end month of the previous quarter
        const endOfQuarter = new Date(d.getFullYear(), previousQuarterEndMonth + 1, 0);
        return endOfQuarter.getFullYear().toString();
    }

    if (frequency === AccountingFrequencyEnum.Monthly) {
        const endOfPreviousMonth = new Date(d.getFullYear(), d.getMonth(), 0); // day 0 of current month = last day of previous month
        return endOfPreviousMonth.getFullYear().toString();
    }

    console.log("Please specify a valid accounting frequency on party instead of " + frequency);
    return "Please specify a valid accounting frequency on party instead of " + frequency;
};
